import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PersonDayService } from '@frontend/AppSettings/services/PipelineProjectService';
import { useBidCapabilities } from '@frontend/BidSheets/useBidCapabilities';

/**
 * Approving a person-day, and the delta a re-run would produce (P7b, migration 0050).
 *
 * THE ACT IS PER PERSON-DAY AND THE RUN IS OVER THE PERIOD. Approving one day runs the calculator
 * from the start of the active period THROUGH that day and writes only that day's hours — the
 * weekly threshold is a running count, so the overtime owed on Thursday depends on Monday through
 * Wednesday.
 *
 * READINESS IS DERIVED AND NAMES ITS BLOCKERS. "Not ready" alone is not actionable, so the panel
 * prints the project-days nobody has verified rather than greying a button with no explanation.
 *
 * SELF-APPROVAL IS ALLOWED AND DISCLOSED (register B4, the owner's decision). An earlier design
 * refused an approver who had authored any of the day's lines; the capability matrix never
 * delivered that separation, and leads enter their own time, so approving a day you authored is
 * ordinary. What replaced the rule is showing that it happened.
 *
 * THE DELTA IS COMPUTED, SHOWN, AND POSTED NOWHERE (decisions D6). What persists is the LOCKED
 * classification; the re-run's own blocks are never written, because persisting them would post
 * the delta by the back door. It lives on its own surface rather than on the reclassification
 * report, whose headline is a disagreement RATE — and a rate cannot be computed when its
 * denominator is three different things.
 */

interface PersonDayApprovalPanelProps {
  employeeid: string;
  employeeName: string;
  workDate: string;
}

const PersonDayApprovalPanel = ({
  employeeid, employeeName, workDate,
}: PersonDayApprovalPanelProps): JSX.Element => {
  const queryClient = useQueryClient();
  const capabilities = useBidCapabilities();
  const mayApprove = capabilities?.capabilities.includes('mutate_bids') ?? false;
  const [error, setError] = useState<string | null>(null);
  const [showDelta, setShowDelta] = useState(false);

  const readiness = useQuery({
    queryKey: ['PersonDayReadiness', employeeid, workDate],
    queryFn: () => PersonDayService.getReadiness(employeeid, workDate),
  });

  const delta = useQuery({
    queryKey: ['PeriodDelta', employeeid, workDate],
    queryFn: () => PersonDayService.getPeriodDelta(employeeid, workDate),
    enabled: showDelta,
  });

  const approve = useMutation({
    mutationFn: () => PersonDayService.approvePersonDay(employeeid, workDate),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['PersonDayReadiness', employeeid] }),
        // An approval changes what a delta re-run compares against — an open delta view keeping
        // pre-approval numbers is a stale reconciliation (R15).
        queryClient.invalidateQueries({ queryKey: ['PeriodDelta', employeeid] }),
        // The ticket list carries `classified_at`, which is what drops this pair out of the
        // approval window — without the refetch, "the remainder appear as these are approved"
        // (the section's own notice) is not true until a manual reload (R4).
        queryClient.invalidateQueries({ queryKey: ['FieldTickets'] }),
      ]);
    },
    // EVERY refusal is a sentence the approver can act on — an unverified project-day names the
    // matter and the day, an unverified ruleset names the jurisdiction, an over-ceiling agreement
    // says the arrangement needs re-recording. Surfacing `e.message` verbatim is the point.
    onError: (e: Error) => setError(e.message),
  });

  const state = readiness.data;

  return (
    <div className="pipeline-card" data-testid="person-day-approval">
      <h4>{employeeName} — {workDate}</h4>
      {error && <div className="pipeline-error" role="alert">{error}</div>}
      {readiness.isLoading && <p>Loading…</p>}
      {/* A failed readiness read must SAY so. Rendering a bare header leaves an approver looking
          at a person-day with no button and no reason, which is indistinguishable from "this day
          is fine and there is nothing to do". */}
      {readiness.isError && (
        <p className="pipeline-error" role="alert">
          Could not read this day&rsquo;s readiness. It has NOT been approved — try again.
        </p>
      )}
      {state && state.classified_at && (
        <p>
          Approved {state.classified_at.slice(0, 10)} · {state.blocks} classified block(s).
          {' '}A classification is final for the period it was locked in; a re-run produces a
          delta rather than a new answer.
        </p>
      )}
      {state && !state.classified_at && (
        <>
          {state.blockers.length > 0 && (
            <ul data-testid="approval-blockers">
              {state.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
            </ul>
          )}
          {!state.ready && state.blockers.length === 0 && (
            // A greyed button with NO named reason reads as broken (the export button's own
            // rule). Practically unreachable today — every not-ready path names a blocker or a
            // status — but a fallback sentence costs nothing and an unnamed refusal costs an
            // afternoon.
            <p className="pipeline-muted">
              This day is not ready for approval ({state.status === 'worked'
                ? 'no blocker was named — reload and try again'
                : `it is recorded as ${state.status}`}).
            </p>
          )}
          <button
            type="button" className="pipeline-primary-btn"
            disabled={!mayApprove || !state.ready || approve.isPending}
            onClick={() => approve.mutate()}
          >
            Approve this day
          </button>
        </>
      )}
      {approve.data?.self_approved && (
        // The disclosure that replaced the withdrawn separation rule. It is not a warning: the
        // act is allowed. It is a record that the approver and the author were the same person.
        <p className="pipeline-muted" data-testid="self-approval-disclosure">
          You approved a day you recorded yourself. That is allowed, and it is noted here so the
          record shows it.
        </p>
      )}
      {approve.data && (
        <p>
          Period {approve.data.period_start} – {approve.data.period_end}
          {' '}({approve.data.period_weeks} week
          {approve.data.period_weeks === 1 ? '' : 's'},{' '}
          {approve.data.period_source === 'agreement'
            ? 'under an averaging agreement' : 'the tenant workweek'}).
          {' '}{approve.data.blocks_written} block(s) written;
          {' '}{approve.data.force_account_swept.lines} force-account line(s) held for release.
        </p>
      )}

      <button
        type="button" className="pipeline-secondary-btn"
        onClick={() => setShowDelta((prev) => !prev)}
      >
        {showDelta ? 'Hide' : 'Show'} period-close delta
      </button>
      {showDelta && delta.data && (
        <div data-testid="period-delta">
          <p>
            Re-running this period today moves overtime by{' '}
            {delta.data.overtime_movement_hours} h against {delta.data.locked_blocks} locked
            block(s). This figure is shown and recorded nowhere — payroll pays what was locked.
          </p>
          {delta.data.unapproved_days.length > 0 && (
            // R13: a mid-period read is a PARTIAL answer and must say so — these days have no
            // locked snapshot to move against, so they are named rather than counted, and a
            // reader must not take a small figure as a finished period.
            <p className="pipeline-muted" data-testid="delta-unapproved-days">
              {delta.data.unapproved_days.length} day(s) in this period are not yet approved and
              are not counted as movement: {delta.data.unapproved_days.join(', ')}.
            </p>
          )}
          <ul>
            {delta.data.days.map((day) => (
              <li key={day.day}>
                {day.day}: {Object.entries(day.movement)
                  .map(([type, hours]) => `${type} ${hours}`).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}
      {showDelta && delta.isError && (
        // "No recorded hours" is a CLAIM about the period. A 403, a 409 or a 500 is not that
        // claim, and printing it as one tells a payroll reader the person worked nothing. The
        // server's own sentence leads (R13) — the 409 enable-window and agreement refusals are
        // actionable, and a generic line swallowed them.
        <p className="pipeline-error" role="alert">
          {(delta.error as Error)?.message || 'Could not compute the delta for this period.'}
          {' '}This is not a statement that the period is empty.
        </p>
      )}
      {/* `isPending` as well as `isLoading`: on the render right after the toggle the gated query
          has not started, so `isLoading` is false and `data` is undefined — one frame of a claim
          that the person worked nothing. `data === null` is the server's GENUINE empty answer
          (the service maps its 404), and under react-query v5 it is the only way this branch can
          render at all — an undefined-resolving queryFn is itself an error (R13). */}
      {showDelta && !delta.isPending && !delta.isLoading && !delta.isError && delta.data === null
        && (
        <p className="pipeline-muted">No recorded hours in the period containing this day.</p>
      )}
    </div>
  );
};

export default PersonDayApprovalPanel;
